import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, message, Spin } from 'antd';
import { CheckCircleOutlined, LoadingOutlined, MessageOutlined, RightOutlined, SendOutlined } from '@ant-design/icons';
import {
  AGENT_REPLY_WAIT_EXTENDED,
  usePhasedAgentReplyWait,
} from '../../hooks/usePhasedAgentReplyWait';
import { useChat } from '../../contexts/ChatContext';
import { useChatUI } from '../../contexts/ChatUIContext';
import { useUser } from '../../contexts/UserContext';
import { useSurfaceContext, type ChatContextMode } from '../../contexts/SurfaceContext';
import { useAppLayout } from '../AppShell/AppLayoutContext';
import { KrybotService } from '../../services/api/krybotService';
import { ModelType } from '../../types/models';
import { RecipientPicker } from './RecipientPicker';
import { ChatPanelAttachmentStrip } from './ChatPanelAttachmentStrip';
import { ChatPanelMessageAttachments } from './ChatPanelMessageAttachments';
import { ChatPanelAssistantMessage } from './ChatPanelAssistantMessage';
import { ChatPanelSystemMessage } from './ChatPanelSystemMessage';
import { ChatPanelPersonMessage } from './ChatPanelPersonMessage';
import { ChatPanelMarkdown } from './ChatPanelMarkdown';
import { ChatPanelSlashMenu } from './ChatPanelSlashMenu';
import { listChatPanelSlashSuggestions } from './chatPanelSlashSuggestions';
import { CommandRegistry } from '../../services/commands/CommandRegistry';
import {
  chatTurnMutatedModel,
  chatWorkspaceToolInvocation,
  notifyControlBridgeModelChanged,
} from '../../utils/chatWorkspaceToolInvocation';
import {
  readPersonDeliveryMetadata,
  readThreadChatPanelRecipients,
  recipientsToPickerValues,
  formatPersonRecipientLabels,
} from './chatPanelPersonMessaging';
import {
  pendingToAttachmentRecord,
  readAttachmentsFromMetadata,
  buildAttachmentMessageMetadata,
  type PendingChatAttachment,
} from './chatPanelAttachments';
import { uploadPendingChatAttachments } from './uploadPendingChatAttachments';
import { routeChatAttachmentExtractions } from './chatPanelExtractionRouting';
import {
  findReusableEmptyDraft,
  formatThreadListLabel,
  sortThreadsForListDisplay,
} from './chatThreadHygiene';
import { parseRecipientValues, useChatRecipientDirectory } from './useChatRecipientDirectory';
import { resolveSurfaceArtifactItemRef } from '../../utils/resolveSurfaceArtifactItemRef';
import { applyChatNavigationAction } from './chatNavigationActions';
import styles from './ChatPanel.module.css';

const CONTEXT_MODES: Array<{ key: ChatContextMode; label: string }> = [
  { key: 'related', label: 'Related' },
  { key: 'tangent', label: 'Tangent' },
  { key: 'general', label: 'General' },
];

function buildThreadName(surfaceLabel: string, contextMode: ChatContextMode): string {
  const prefix = contextMode === 'related' ? surfaceLabel : contextMode === 'tangent' ? 'Tangent' : 'General';
  return `${prefix} · ${new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
}

interface ChatSelectedWorkView {
  title: string;
  actor?: string;
  stage?: string;
  canOpenInExo: boolean;
}

function readChatSelectedWork(metadata: Record<string, unknown> | undefined): ChatSelectedWorkView | null {
  const raw = metadata?.selectedWork;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const rec = raw as Record<string, unknown>;
  const title = typeof rec.title === 'string' ? rec.title.trim() : '';
  if (!title) return null;
  const actor = typeof rec.actor === 'string' ? rec.actor.trim() : '';
  const stage = typeof rec.stage === 'string' ? rec.stage.trim() : '';
  return {
    title,
    actor: actor || undefined,
    stage: stage || undefined,
    canOpenInExo: rec.canOpenInExo === true,
  };
}

type SendPhase = 'idle' | 'preparing' | 'sent' | 'krybot_working' | 'error';

interface SendStatus {
  phase: SendPhase;
  detail?: string;
  errorMessage?: string;
}

export const ChatPanel: React.FC = () => {
  const { chatPanelExpanded, toggleChatPanelExpanded, expandChatPanel } = useAppLayout();
  const navigate = useNavigate();
  const { effectiveContext, contextMode, setContextMode } = useSurfaceContext();
  const { options } = useChatRecipientDirectory();
  const { user, selectedCompany, selectCompany } = useUser();
  const { model: chatUIModel, uiRole: chatUIRole } = useChatUI();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    threads,
    messages,
    currentThreadId,
    createThread,
    selectThread,
    updateThread,
    loadMessages,
    loadUserThreads,
    sendMessage,
    addMessage,
    isLoading,
    isLoadingMessages,
    isTyping,
  } = useChat();

  const [recipientValues, setRecipientValues] = useState<string[]>([]);
  const [threadMode, setThreadMode] = useState<'new' | 'resume'>('new');
  const [resumeThreadId, setResumeThreadId] = useState<string | null>(null);
  const [showRecent, setShowRecent] = useState(false);
  const [draft, setDraft] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingChatAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<SendStatus>({ phase: 'idle' });
  const selectThreadRef = useRef(selectThread);
  const lastResumeSelectRef = useRef<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  selectThreadRef.current = selectThread;

  const suggestedValues = useMemo(
    () => (effectiveContext.suggestedRecipients ?? []).map((r) => `${r.kind}:${r.id}`),
    [effectiveContext.suggestedRecipients]
  );

  const surfaceArtifactItemRef = useMemo(
    () => resolveSurfaceArtifactItemRef(effectiveContext),
    [effectiveContext]
  );

  const selectedWork = useMemo(
    () => readChatSelectedWork(effectiveContext.metadata),
    [effectiveContext.metadata]
  );

  const selectedWorkItemRef = useMemo(() => {
    const fromMeta =
      typeof effectiveContext.metadata?.artifactItemRef === 'string'
        ? effectiveContext.metadata.artifactItemRef.trim()
        : '';
    return fromMeta || surfaceArtifactItemRef || '';
  }, [effectiveContext.metadata, surfaceArtifactItemRef]);

  const openSelectedWorkInExo = useCallback(() => {
    const metadata = effectiveContext.metadata ?? {};
    const itemRef = selectedWorkItemRef;
    if (!itemRef) return;
    const slug =
      typeof metadata.workspaceSlug === 'string' ? metadata.workspaceSlug.trim() : '';
    applyChatNavigationAction(
      {
        type: 'open_control_bridge',
        label: 'Open in Exo',
        route: slug
          ? `/render/${encodeURIComponent(slug)}/control-bridge`
          : effectiveContext.route,
        workspaceSlug: slug || undefined,
        surface: 'exopanel',
        artifactItemRef: itemRef,
      },
      navigate
    );
  }, [
    effectiveContext.metadata,
    effectiveContext.route,
    navigate,
    selectedWorkItemRef,
  ]);

  const primaryAgentValue = useMemo(() => {
    const agents = options.filter((opt) => opt.kind === 'agent');
    const preferred =
      agents.find((a) => /company.?agent|krybot/i.test(a.label)) ??
      agents.find((a) => /chat/i.test(a.label)) ??
      agents[0];
    return preferred?.value;
  }, [options]);

  useEffect(() => {
    if (threadMode === 'resume') return;
    if (contextMode === 'general') {
      setRecipientValues([]);
      return;
    }
    const personValues = suggestedValues.filter((v) => !v.startsWith('agent:'));
    const agentValues = primaryAgentValue ? [primaryAgentValue] : [];
    const merged = [...agentValues, ...personValues.filter((v) => !agentValues.includes(v))];
    setRecipientValues(merged);
  }, [contextMode, suggestedValues.join('|'), threadMode, primaryAgentValue]);

  const resumedThread = useMemo(
    () => (resumeThreadId ? threads.find((t) => t.id === resumeThreadId) : undefined),
    [threads, resumeThreadId]
  );

  const resumedThreadRecipients = useMemo(
    () => (resumedThread ? readThreadChatPanelRecipients(resumedThread) : []),
    [resumedThread]
  );

  useEffect(() => {
    if (threadMode !== 'resume' || resumedThreadRecipients.length === 0) return;
    // Restore stored recipients only — do not force-add KRYBOT (that blocked person-deliver).
    setRecipientValues(recipientsToPickerValues(resumedThreadRecipients));
  }, [threadMode, resumedThread?.id, resumedThreadRecipients]);

  useEffect(() => {
    // Keep the active resume thread across Control Bridge surfaces so the sender
    // still sees their own General / prior threads in the list and transcript.
    setPendingAttachments([]);
  }, [effectiveContext.surfaceId]);

  // Deep link from email / share: ?chatThreadId=<uuid>[&companyId=…]
  const deepLinkHandledRef = useRef<string | null>(null);
  useEffect(() => {
    const raw = searchParams.get('chatThreadId')?.trim();
    if (!raw || deepLinkHandledRef.current === raw) return;
    deepLinkHandledRef.current = raw;
    const companyFromLink = searchParams.get('companyId')?.trim();
    if (companyFromLink && companyFromLink !== selectedCompany?.id) {
      void selectCompany(companyFromLink);
    }
    expandChatPanel();
    setThreadMode('resume');
    setResumeThreadId(raw);
    setShowRecent(true);
    void selectThread(raw);
    const next = new URLSearchParams(searchParams);
    next.delete('chatThreadId');
    setSearchParams(next, { replace: true });
  }, [
    searchParams,
    expandChatPanel,
    selectThread,
    setSearchParams,
    selectCompany,
    selectedCompany?.id,
  ]);

  const recentThreads = useMemo(() => {
    // Do not hide the operator's own threads behind the current surface filter.
    return sortThreadsForListDisplay(threads).slice(0, 12);
  }, [threads]);

  const activeThreadId = threadMode === 'resume' ? resumeThreadId : currentThreadId;

  const visibleMessages = useMemo(() => {
    const threadId = activeThreadId ?? currentThreadId;
    if (!threadId) return [];
    return messages.filter((m) => m.threadId === threadId);
  }, [messages, activeThreadId, currentThreadId]);

  const messagesScrollKey = useMemo(() => {
    const shown = visibleMessages.slice(-12);
    const last = shown[shown.length - 1];
    if (!last) return `empty:${isTyping ? 1 : 0}`;
    return `${last.id}:${(last.text || '').length}:${isTyping ? 1 : 0}:${sendStatus.phase}`;
  }, [visibleMessages, isTyping, sendStatus.phase]);

  // Keep the Messages pane pinned to the latest turn so operators do not scroll
  // after each send / assistant reply / typing indicator.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messagesScrollKey, chatPanelExpanded]);

  useEffect(() => {
    if (threadMode !== 'resume' || !resumeThreadId) {
      if (threadMode === 'new') {
        lastResumeSelectRef.current = null;
      }
      return;
    }
    if (lastResumeSelectRef.current === resumeThreadId) {
      return;
    }
    lastResumeSelectRef.current = resumeThreadId;
    void selectThreadRef.current(resumeThreadId);
  }, [threadMode, resumeThreadId]);

  useEffect(() => {
    if (isTyping) {
      setSendStatus((prev) =>
        prev.phase === 'error' ? prev : { phase: 'krybot_working' }
      );
    } else {
      setSendStatus((prev) =>
        prev.phase === 'krybot_working' ? { phase: 'idle' } : prev
      );
    }
  }, [isTyping]);

  useEffect(() => {
    if (sendStatus.phase !== 'sent') return;
    const timer = window.setTimeout(() => {
      setSendStatus((prev) => (prev.phase === 'sent' ? { phase: 'idle' } : prev));
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [sendStatus.phase]);

  const krybotWait = usePhasedAgentReplyWait(sendStatus.phase === 'krybot_working', {
    initialMessage: 'Krybot is working…',
    extendedMessage: AGENT_REPLY_WAIT_EXTENDED,
  });

  const slashSuggestions = useMemo(() => {
    if (!draft.startsWith('/')) return [];
    const commands = CommandRegistry.getInstance()
      .getAllCommands()
      .map((cmd) => ({
        name: cmd.name,
        description: cmd.description,
        usage: cmd.usage,
      }));
    return listChatPanelSlashSuggestions(draft, commands);
  }, [draft]);

  const handleSend = useCallback(async () => {
    const text = draft.trim();
    const attachmentsForSend = pendingAttachments;
    if ((!text && attachmentsForSend.length === 0) || sending) return;

    setSending(true);
    setDraft('');
    setPendingAttachments([]);
    setSendStatus({ phase: 'preparing', detail: 'Preparing…' });
    try {
      let threadId = activeThreadId;
      const recipients = parseRecipientValues(recipientValues, options);
      const agentRecipients = recipients.filter((r) => r.kind === 'agent');
      const personRecipients = recipients.filter((r) => r.kind === 'person');
      const shouldPersonDeliver = personRecipients.length > 0;
      const shouldKrybotTurn = agentRecipients.length > 0 || !shouldPersonDeliver;

      const threadContextPayload = {
        companyId: selectedCompany?.id,
        surfaceId: effectiveContext.surfaceId,
        surfaceLabel: effectiveContext.surfaceLabel,
        route: effectiveContext.route,
        contextMode,
        chatPanelRecipients: recipients,
        ...(effectiveContext.metadata ?? {}),
      };

      if (threadMode === 'new' || !threadId) {
        setSendStatus({ phase: 'preparing', detail: 'Creating thread…' });
        const model =
          chatUIModel ??
          ({
            type: ModelType.COMPANY,
            artifactTreeId: selectedCompany?.id,
          } as const);

        const threadDisplayName = buildThreadName(effectiveContext.surfaceLabel, contextMode);
        // Prefer a local empty default-named draft so we don't mint another Krybot Chat.
        const reusable = findReusableEmptyDraft(threads);
        if (reusable) {
          threadId = reusable.id;
          try {
            await updateThread(reusable.id, {
              name: threadDisplayName,
              description: effectiveContext.summary,
              context: threadContextPayload,
              model,
              uiRole: chatUIRole || 'operator',
            });
          } catch (persistErr) {
            console.warn('[ChatPanel] failed to rename reusable draft', persistErr);
          }
        } else {
          const thread = await createThread(
            threadDisplayName,
            effectiveContext.summary,
            model,
            chatUIRole || 'operator',
            threadContextPayload
          );
          if (!thread?.id) {
            throw new Error('Could not start a chat thread.');
          }
          threadId = thread.id;

          try {
            await updateThread(thread.id, {
              name: threadDisplayName,
              description: effectiveContext.summary,
              context: threadContextPayload,
              model,
              uiRole: chatUIRole || 'operator',
            });
          } catch (persistErr) {
            console.warn('[ChatPanel] failed to persist thread name/context', persistErr);
          }
        }
      } else if (threadId) {
        // Keep durable thread context aligned with current CB workspace focus so
        // later turns / new agent assigns do not re-orient on a prior company.
        try {
          await updateThread(threadId, {
            context: threadContextPayload,
            description: effectiveContext.summary,
          });
        } catch (persistErr) {
          console.warn('[ChatPanel] failed to refresh thread context', persistErr);
        }
      }

      if (threadId && shouldKrybotTurn) {
        setSendStatus({ phase: 'preparing', detail: 'Connecting to Krybot…' });
        if (agentRecipients.length > 0) {
          let assigned = false;
          for (const agent of agentRecipients) {
            try {
              await KrybotService.assignAgent(threadId, agent.id);
              assigned = true;
              break;
            } catch (assignErr) {
              console.warn('[ChatPanel] agent assign failed', assignErr);
            }
          }
          if (!assigned) {
            try {
              await KrybotService.ensureChatAgentAssigned(
                threadId,
                { ui_role: chatUIRole || 'operator' },
                user?.id,
                chatUIRole || 'operator'
              );
            } catch (assignErr) {
              console.warn('[ChatPanel] ensure chat agent failed', assignErr);
            }
          }
        } else {
          try {
            await KrybotService.ensureChatAgentAssigned(
              threadId,
              { ui_role: chatUIRole || 'operator' },
              user?.id,
              chatUIRole || 'operator'
            );
          } catch (assignErr) {
            console.warn('[ChatPanel] ensure chat agent failed', assignErr);
          }
        }
      }

      let attachmentRecords = attachmentsForSend.map(pendingToAttachmentRecord);
      if (attachmentsForSend.length > 0 && threadId && user?.id) {
        attachmentRecords = await uploadPendingChatAttachments(threadId, attachmentsForSend, {
          userId: user.id,
          ...(surfaceArtifactItemRef ? { artifactItemRef: surfaceArtifactItemRef } : {}),
        });
      }

      const messageMetadata =
        attachmentRecords.length > 0
          ? buildAttachmentMessageMetadata(attachmentRecords)
          : undefined;

      if (attachmentRecords.length > 0 && threadId && user?.id) {
        await routeChatAttachmentExtractions(threadId, attachmentRecords, {
          userId: user.id,
          ...(surfaceArtifactItemRef ? { artifactItemRef: surfaceArtifactItemRef } : {}),
        });
      }

      if (shouldPersonDeliver && threadId && user?.id) {
        setSendStatus({ phase: 'preparing', detail: 'Sending message…' });
        const deliverReceipt = await KrybotService.deliverPersonMessage(threadId, {
          user_id: user.id,
          message_text: text,
          message_metadata: {
            ...(messageMetadata ?? {}),
            context: threadContextPayload,
          },
          chat_panel_recipients: recipients.map((recipient) => ({
            kind: recipient.kind,
            id: recipient.id,
            label: recipient.label,
          })),
        });
        const um = deliverReceipt?.user_message;
        if (um?.id) {
          addMessage({
            id: um.id,
            threadId,
            text: um.content || text,
            who: 'user',
            type: 'text',
            metadata: {
              ...(um.metadata && typeof um.metadata === 'object' ? um.metadata : {}),
              delivery_mode: 'person_deliver',
              chat_panel_recipients: recipients.map((r) => ({
                kind: r.kind,
                id: r.id,
                label: r.label,
              })),
            },
          });
        }
      }

      if (shouldKrybotTurn && threadId) {
        setSendStatus({
          phase: shouldPersonDeliver ? 'krybot_working' : 'preparing',
          detail: shouldPersonDeliver ? 'Krybot is working…' : 'Sending message…',
        });
        const turnWorkspaceSlug =
          typeof effectiveContext.metadata?.workspaceSlug === 'string'
            ? effectiveContext.metadata.workspaceSlug.trim()
            : '';
        const krybotMetadata = {
          ...(messageMetadata ?? {}),
          context: threadContextPayload,
          surfaceId: effectiveContext.surfaceId,
          surfaceLabel: effectiveContext.surfaceLabel,
          route: effectiveContext.route,
          ...(effectiveContext.metadata ?? {}),
          ...(turnWorkspaceSlug
            ? { workspaceSlug: turnWorkspaceSlug, workspace_slug: turnWorkspaceSlug }
            : {}),
          chat_panel_recipients: recipients.map((r) => ({
            kind: r.kind,
            id: r.id,
            label: r.label,
          })),
          ...(shouldPersonDeliver ? { dual_delivery: true } : {}),
        };
        // Dual path: person row already persisted; Krybot turn still needs the text.
        // sendMessage also optimistic-appends — skip when person already rendered.
        if (shouldPersonDeliver) {
          if (!user?.id) {
            throw new Error('User id required for Krybot dual-delivery turn');
          }
          const dualWorkspaceSlug =
            typeof krybotMetadata.workspaceSlug === 'string'
              ? krybotMetadata.workspaceSlug
              : undefined;
          const dualTools = chatWorkspaceToolInvocation(dualWorkspaceSlug);
          const dualTurn = await KrybotService.sendMessage(threadId, {
            thread_id: threadId,
            user_id: user.id,
            message_text: text,
            message_metadata: krybotMetadata,
            ...(dualTools ? { tool_invocation: dualTools } : {}),
          });
          if (chatTurnMutatedModel(dualTurn)) {
            notifyControlBridgeModelChanged(dualWorkspaceSlug);
          }
          await loadMessages(threadId);
        } else {
          await sendMessage(text, {
            type: 'text',
            metadata: krybotMetadata,
            threadId: threadId ?? undefined,
          });
        }
      } else if (shouldPersonDeliver && threadId) {
        await loadMessages(threadId);
      }

      setSendStatus({ phase: 'sent' });
      if (threadId) {
        setThreadMode('resume');
        setResumeThreadId(threadId);
        selectThread(threadId);
      }
      // Refresh list so auto-title / hygiene markers from backend appear.
      void loadUserThreads();
    } catch (err) {
      console.error('[ChatPanel] send failed', err);
      const errText =
        err instanceof Error ? err.message : 'Could not send message. Try again.';
      setDraft(text);
      setPendingAttachments(attachmentsForSend);
      setSendStatus({ phase: 'error', errorMessage: errText });
      void message.error(errText);
    } finally {
      setSending(false);
    }
  }, [
    draft,
    sending,
    pendingAttachments,
    activeThreadId,
    recipientValues,
    options,
    threadMode,
    chatUIModel,
    chatUIRole,
    selectedCompany?.id,
    effectiveContext,
    contextMode,
    createThread,
    selectThread,
    updateThread,
    sendMessage,
    loadMessages,
    loadUserThreads,
    threads,
    user?.id,
    surfaceArtifactItemRef,
    addMessage,
  ]);

  if (!user) return null;

  if (!chatPanelExpanded) {
    return (
      <button
        type="button"
        className={styles.reveal}
        aria-label="Open chat panel"
        title="Open chat panel"
        onClick={toggleChatPanelExpanded}
      >
        <MessageOutlined />
        Chat
      </button>
    );
  }

  return (
    <aside className={styles.panel} data-testid="chat-panel" aria-label="Chat panel">
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Chat</h2>
        <button
          type="button"
          className={styles.collapseBtn}
          aria-label="Collapse chat panel"
          title="Collapse chat panel"
          onClick={toggleChatPanelExpanded}
        >
          <RightOutlined />
        </button>
      </div>

      {sendStatus.phase !== 'idle' ? (
        <div
          className={`${styles.statusBar} ${styles[`status_${sendStatus.phase}`]}`}
          role="status"
          aria-live="polite"
          data-testid="chat-send-status"
        >
          {sendStatus.phase === 'preparing' ? (
            <>
              <Spin indicator={<LoadingOutlined spin />} size="small" />
              <span>{sendStatus.detail ?? 'Working…'}</span>
            </>
          ) : null}
          {sendStatus.phase === 'sent' ? (
            <>
              <CheckCircleOutlined aria-hidden />
              <span>Message sent</span>
            </>
          ) : null}
          {sendStatus.phase === 'krybot_working' ? (
            <>
              <Spin indicator={<LoadingOutlined spin />} size="small" />
              <span>{krybotWait.message}</span>
            </>
          ) : null}
          {sendStatus.phase === 'error' ? (
            <span>{sendStatus.errorMessage ?? 'Message could not be sent.'}</span>
          ) : null}
        </div>
      ) : null}

      <div className={styles.body}>
        <section>
          <div className={styles.sectionLabel}>Context</div>
          <div className={styles.contextCard}>
            <p className={styles.contextTitle}>{effectiveContext.surfaceLabel}</p>
            {effectiveContext.summary ? (
              <p className={styles.contextSummary}>{effectiveContext.summary}</p>
            ) : null}
            <div className={styles.modeRow} role="group" aria-label="Message context mode">
              {CONTEXT_MODES.map((mode) => (
                <button
                  key={mode.key}
                  type="button"
                  className={`${styles.modeChip} ${contextMode === mode.key ? styles.modeChipActive : ''}`}
                  onClick={() => setContextMode(mode.key)}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
          {selectedWork ? (
            <div className={styles.selectedWorkCard} data-testid="chat-selected-work">
              <p className={styles.selectedWorkTitle}>{selectedWork.title}</p>
              {selectedWork.actor || selectedWork.stage ? (
                <p className={styles.selectedWorkMeta}>
                  {[selectedWork.actor, selectedWork.stage].filter(Boolean).join(' · ')}
                </p>
              ) : null}
              {selectedWork.canOpenInExo && selectedWorkItemRef ? (
                <button
                  type="button"
                  className={styles.openInExoLink}
                  data-testid="chat-open-in-exo"
                  onClick={openSelectedWorkInExo}
                >
                  Open in Exo →
                </button>
              ) : null}
            </div>
          ) : null}
        </section>

        <section>
          <div className={styles.sectionLabel}>Thread</div>
          <div className={styles.threadRow}>
            {threadMode === 'new' ? (
              <span className={styles.newThreadBadge}>New thread</span>
            ) : (
              <span className={styles.newThreadBadge}>Resuming thread</span>
            )}
            {threadMode === 'resume' ? (
              <Button size="small" type="link" onClick={() => { setThreadMode('new'); setResumeThreadId(null); }}>
                Start new instead
              </Button>
            ) : null}
          </div>
          {threadMode === 'resume' && resumedThreadRecipients.length > 0 ? (
            <p className={styles.threadRecipientsHint}>
              Participants: {formatPersonRecipientLabels(resumedThreadRecipients)}
            </p>
          ) : null}
          <button type="button" className={styles.recentToggle} onClick={() => setShowRecent((v) => !v)}>
            {showRecent ? 'Hide recent threads' : 'Resume a recent thread…'}
          </button>
          {showRecent ? (
            <div className={styles.recentList}>
              {recentThreads.length === 0 ? (
                <p className={styles.emptyHint}>No recent threads yet.</p>
              ) : (
                recentThreads.map((thread) => (
                  <button
                    key={thread.id}
                    type="button"
                    className={`${styles.recentItem} ${resumeThreadId === thread.id ? styles.recentItemActive : ''} ${thread.isEmpty ? styles.recentItemEmpty : ''}`}
                    onClick={() => {
                      setThreadMode('resume');
                      setResumeThreadId(thread.id);
                      setShowRecent(false);
                    }}
                  >
                    {formatThreadListLabel(thread)}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </section>

        <section>
          <div className={styles.sectionLabel}>To</div>
          <RecipientPicker options={options} value={recipientValues} onChange={setRecipientValues} />
        </section>

        <section>
          <div className={styles.sectionLabel}>Messages</div>
          <div
            className={styles.messages}
            ref={messagesContainerRef}
            data-testid="chat-panel-messages"
          >
            {isLoadingMessages ? (
              <p className={styles.emptyHint}>Loading…</p>
            ) : visibleMessages.length === 0 && !isTyping ? (
              <p className={styles.emptyHint}>
                {threadMode === 'new' && !activeThreadId
                  ? 'New thread — your first message starts the conversation.'
                  : 'No messages in this thread yet.'}
              </p>
            ) : (
              <>
              {visibleMessages.slice(-12).map((msg) => {
                if (msg.who === 'system') {
                  return (
                    <ChatPanelSystemMessage
                      key={msg.id}
                      text={msg.text}
                      metadata={msg.metadata as Record<string, unknown> | undefined}
                    />
                  );
                }
                const personDelivery =
                  msg.who === 'user' ? readPersonDeliveryMetadata(msg.metadata as Record<string, unknown>) : null;
                if (personDelivery) {
                  return (
                    <ChatPanelPersonMessage
                      key={msg.id}
                      text={msg.text}
                      metadata={msg.metadata as Record<string, unknown> | undefined}
                    />
                  );
                }
                if (msg.who === 'assistant') {
                  return (
                    <ChatPanelAssistantMessage
                      key={msg.id}
                      text={msg.text}
                      type={msg.type}
                      metadata={msg.metadata as Record<string, unknown> | undefined}
                    />
                  );
                }
                return (
                <div key={msg.id} className={styles.messageBubble}>
                  <span className={styles.messageWho}>You:</span>
                  {msg.text ? <ChatPanelMarkdown text={msg.text} /> : null}
                  <ChatPanelMessageAttachments
                    attachments={readAttachmentsFromMetadata(
                      msg.metadata as Record<string, unknown> | undefined
                    )}
                  />
                </div>
              );
              })}
              {isTyping && sendStatus.phase !== 'krybot_working' ? (
                <p className={styles.emptyHint} role="status" aria-live="polite">
                  Assistant is typing…
                </p>
              ) : null}
              <div ref={messagesEndRef} data-testid="chat-panel-messages-end" aria-hidden />
              </>
            )}
          </div>
        </section>
      </div>

      <div className={styles.composeRow}>
        <ChatPanelAttachmentStrip
          attachments={pendingAttachments}
          onChange={setPendingAttachments}
          disabled={sending || isLoading}
        />
        <ChatPanelSlashMenu
          suggestions={slashSuggestions}
          onPick={(command) => setDraft(`/${command} `)}
        />
        <Input.TextArea
          className={styles.composeInput}
          rows={3}
          value={draft}
          placeholder="Message about this surface, or / for commands…"
          disabled={sending || isLoading}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
          }}
        />
        <Button
          type="primary"
          className={styles.sendBtn}
          icon={<SendOutlined />}
          loading={sending}
          disabled={(!draft.trim() && pendingAttachments.length === 0) || sending}
          onClick={() => void handleSend()}
        >
          Send
        </Button>
      </div>
    </aside>
  );
};

export default ChatPanel;
